import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Chip, Skeleton,
  Avatar, Divider, IconButton, Tooltip, Select, MenuItem, FormControl,
  InputLabel,
} from '@mui/material';
import {
  People, DirectionsCar, AssignmentTurnedIn, VerifiedUser,
  ReportProblem, ConfirmationNumber, BatteryChargingFull,
  BadgeOutlined, SwapHoriz, Refresh, TrendingUp,
  ElectricScooter, Map,
} from '@mui/icons-material';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, ResponsiveContainer, Legend, PieChart,
  Pie, Cell,
} from 'recharts';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';
import { useAuth } from '../context/AuthContext';

// ── Stat card ─────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon: Icon, color, loading }) => (
  <Card sx={{
    borderRadius: 1,
    bgcolor: `${color}11`,
    border: `1px solid ${color}`,
    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
    overflow: 'hidden',
    transition: 'transform 0.15s, box-shadow 0.15s',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 24px rgba(0,0,0,0.12)' },
  }}>
    <CardContent sx={{ p: 2 }}>
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Skeleton variant="rounded" width={44} height={44} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="55%" height={28} />
            <Skeleton width="80%" height={14} />
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Icon bubble */}
          <Box sx={{
            width: 44, height: 44, borderRadius: 1,
            background: `linear-gradient(135deg, ${color}22, ${color}22)`,
            border: `1px solid ${color}30`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Icon sx={{ color, fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h5" fontWeight={800} sx={{ color: '#0f172a', lineHeight: 1.15 }}>
              {(value ?? 0).toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={500} noWrap>
              {label}
            </Typography>
          </Box>
        </Box>
      )}
    </CardContent>
  </Card>
  // <Card sx={{
  //   borderRadius: 1,
  //   boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
  //   overflow: 'hidden',
  //   transition: 'transform 0.15s, box-shadow 0.15s',
  //   '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 24px rgba(0,0,0,0.12)' },
  // }}>
  //   {/* Top colour strip */}
  //   <Box sx={{ height: 4, background: `linear-gradient(90deg, ${color}, ${color}88)` }} />
  //   <CardContent sx={{ p: 2 }}>
  //     {loading ? (
  //       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
  //         <Skeleton variant="rounded" width={44} height={44} />
  //         <Box sx={{ flex: 1 }}>
  //           <Skeleton width="55%" height={28} />
  //           <Skeleton width="80%" height={14} />
  //         </Box>
  //       </Box>
  //     ) : (
  //       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
  //         {/* Icon bubble */}
  //         <Box sx={{
  //           width: 44, height: 44, borderRadius: 2,
  //           background: `linear-gradient(135deg, ${color}22, ${color}11)`,
  //           border: `1px solid ${color}30`,
  //           display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  //         }}>
  //           <Icon sx={{ color, fontSize: 22 }} />
  //         </Box>
  //         <Box sx={{ flex: 1, minWidth: 0 }}>
  //           <Typography variant="h5" fontWeight={800} sx={{ color: '#0f172a', lineHeight: 1.15 }}>
  //             {(value ?? 0).toLocaleString()}
  //           </Typography>
  //           <Typography variant="caption" color="text.secondary" fontWeight={500} noWrap>
  //             {label}
  //           </Typography>
  //         </Box>
  //       </Box>
  //     )}
  //   </CardContent>
  // </Card>
);

// ── Section header ────────────────────────────────────────────────────────────
const SectionHeader = ({ title, icon: Icon }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
    <Icon sx={{ fontSize: 18, color: '#1a237e' }} />
    <Typography variant="subtitle1" fontWeight={700} color="#1a237e">{title}</Typography>
  </Box>
);

const KYC_COLOR = { pending: 'warning', approved: 'success', rejected: 'error', review: 'info' };
const PIE_COLORS = ['#16a34a', '#1565c0', '#d97706', '#dc2626'];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleFilter, setVehicleFilter] = useState('all'); // all, active, assigned, maintenance
  const [loadingVehicles, setLoadingVehicles] = useState(true);

  const loadStats = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    const res = await InvokeApi(Config.base_url + apiList.getDashboardStats, {});
    if (res?.code === '200') setStats(res.stats ?? {});
    setLoading(false); setRefreshing(false);
  }, []);

  const loadVehicles = useCallback(async () => {
    setLoadingVehicles(true);
    const res = await InvokeApi(Config.base_url + apiList.getVehicles, {});
    if (res?.code === '200') {
      setVehicles(res.vehicles ?? []);
    }
    setLoadingVehicles(false);
  }, []);

  useEffect(() => { 
    loadStats(); 
    loadVehicles();
  }, [loadStats, loadVehicles]);

  const reg = stats?.registrations_chart ?? [];
  const comp = stats?.complaints_chart ?? [];
  const recentKYC = stats?.recent_kyc ?? [];
  const recentAssign = stats?.recent_assignments ?? [];

  // Vehicle status pie data
  const vehiclePie = [
    { name: 'Available', value: (stats?.total_vehicles ?? 0) - (stats?.assigned_vehicles ?? 0) - (stats?.maintenance_vehicles ?? 0) },
    { name: 'Assigned', value: stats?.assigned_vehicles ?? 0 },
    { name: 'Maintenance', value: stats?.maintenance_vehicles ?? 0 },
    { name: 'Retired', value: stats?.retired_vehicles ?? 0 },
  ].filter(d => d.value > 0);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <Box>
      {/* ── Welcome banner ─────────────────────────────────────────────────── */}
      <Box sx={{
        mb: 3, p: 3, borderRadius: 1,
        background: 'linear-gradient(135deg, #1a237e 0%, #1565c0 60%, #00acc1 100%)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2,
      }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#fff">
            {greeting}, {user?.name || 'Admin'} 👋
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.3 }}>
            Here's what's happening with Ridexcity today.
          </Typography>
        </Box>
        <Tooltip title={refreshing ? 'Refreshing…' : 'Refresh stats'}>
          <IconButton
            onClick={() => loadStats(true)}
            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
          >
            <Refresh sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Stat cards ──────────────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { key: 'total_users', label: 'Total Users', Icon: People, color: '#1a237e' },
          { key: 'total_vehicles', label: 'Total Vehicles', Icon: ElectricScooter, color: '#1565c0' },
          { key: 'assigned_vehicles', label: 'Assigned Vehicles', Icon: AssignmentTurnedIn, color: '#16a34a' },
          { key: 'kyc_pending', label: 'KYC Pending', Icon: VerifiedUser, color: '#d97706' },
          { key: 'open_complaints', label: 'Open Complaints', Icon: ReportProblem, color: '#dc2626' },
          { key: 'open_tickets', label: 'Open Tickets', Icon: ConfirmationNumber, color: '#7c3aed' },
          { key: 'total_stations', label: 'Battery Stations', Icon: BatteryChargingFull, color: '#0891b2' },
          { key: 'total_employees', label: 'Total Employees', Icon: BadgeOutlined, color: '#64748b' },
        ].map(({ key, label, Icon, color }) => (
          <Grid item xs={6} sm={4} md={3} key={key}>
            <StatCard
              label={label}
              value={stats?.[key]}
              icon={Icon}
              color={color}
              loading={loading}
            />
          </Grid>))}
      </Grid>

      {/* ── Charts row ──────────────────────────────────────────────────────── */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* Registrations bar chart */}
        <Grid item xs={12} md={5}>
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 2.5, height: '100%' }}>
            <SectionHeader title="User Registrations (6 months)" icon={TrendingUp} />
            {loading
              ? <Skeleton variant="rectangular" height={210} sx={{ borderRadius: 2 }} />
              : reg.length === 0
                ? <Box sx={{ height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary" variant="body2">No data available</Typography>
                </Box>
                : <ResponsiveContainer width="100%" height={210}>
                  <BarChart data={reg} margin={{ top: 4, right: 4, bottom: 0, left: -16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <ReTooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="users" name="Users" radius={[4, 4, 0, 0]}
                      fill="url(#barGrad)" />
                    <defs>
                      <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#1a237e" />
                        <stop offset="100%" stopColor="#1565c0" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
            }
          </Card>
        </Grid>

        {/* Complaints trend */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 2.5, height: '100%' }}>
            <SectionHeader title="Complaints Trend" icon={ReportProblem} />
            {loading
              ? <Skeleton variant="rectangular" height={210} sx={{ borderRadius: 2 }} />
              : comp.length === 0
                ? <Box sx={{ height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary" variant="body2">No data available</Typography>
                </Box>
                : <ResponsiveContainer width="100%" height={210}>
                  <LineChart data={comp} margin={{ top: 4, right: 4, bottom: 0, left: -16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <ReTooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="open" stroke="#dc2626" strokeWidth={2} dot={false} name="Open" />
                    <Line type="monotone" dataKey="resolved" stroke="#16a34a" strokeWidth={2} dot={false} name="Resolved" />
                  </LineChart>
                </ResponsiveContainer>
            }
          </Card>
        </Grid>

        {/* Vehicle status pie */}
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 2.5, height: '100%' }}>
            <SectionHeader title="Vehicle Status" icon={DirectionsCar} />
            {loading
              ? <Skeleton variant="rectangular" height={210} sx={{ borderRadius: 2 }} />
              : vehiclePie.length === 0
                ? <Box sx={{ height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary" variant="body2">No vehicles</Typography>
                </Box>
                : <>
                  <ResponsiveContainer width="100%" height={140}>
                    <PieChart>
                      <Pie data={vehiclePie} cx="50%" cy="50%" innerRadius={40} outerRadius={65}
                        paddingAngle={3} dataKey="value">
                        {vehiclePie.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <ReTooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ mt: 1 }}>
                    {vehiclePie.map((d, i) => (
                      <Box key={d.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PIE_COLORS[i % PIE_COLORS.length] }} />
                          <Typography variant="caption" color="text.secondary">{d.name}</Typography>
                        </Box>
                        <Typography variant="caption" fontWeight={700}>{d.value}</Typography>
                      </Box>
                    ))}
                  </Box>
                </>
            }
          </Card>
        </Grid>
      </Grid>

      {/* ── Bottom row: KYC + Activity ───────────────────────────────────────── */}
      <Grid container spacing={2.5}>
        {/* Recent KYC */}
        <Grid item xs={12} lg={7}>
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
              <SectionHeader title="Recent KYC Submissions" icon={VerifiedUser} />
            </Box>
            {loading ? (
              <Box sx={{ px: 2.5, pb: 2 }}>
                {[...Array(4)].map((_, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center' }}>
                    <Skeleton variant="circular" width={36} height={36} />
                    <Box sx={{ flex: 1 }}><Skeleton width="60%" /><Skeleton width="40%" height={12} /></Box>
                    <Skeleton width={70} height={24} sx={{ borderRadius: 99 }} />
                  </Box>
                ))}
              </Box>
            ) : recentKYC.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No recent KYC submissions</Typography>
              </Box>
            ) : (
              <Box>
                {recentKYC.map((k, i) => (
                  <Box key={k.id ?? i}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.5 }}>
                      <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontSize: 14, fontWeight: 700 }}>
                        {(k.name || k.user_name || 'U')[0].toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{k.name || k.user_name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {k.phone || k.user_phone || '—'}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                        <Chip label={k.status || k.kyc_status || 'pending'} color={KYC_COLOR[k.status || k.kyc_status] || 'default'} size="small" sx={{ fontWeight: 600 }} />
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.3 }}>
                          {k.submitted_at ? new Date(k.submitted_at).toLocaleDateString() : '—'}
                        </Typography>
                      </Box>
                    </Box>
                    {i < recentKYC.length - 1 && <Divider sx={{ mx: 2.5 }} />}
                  </Box>
                ))}
              </Box>
            )}
          </Card>
        </Grid>

        {/* Recent vehicle activity */}
        <Grid item xs={12} lg={5}>
          <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
              <SectionHeader title="Recent Vehicle Activity" icon={SwapHoriz} />
            </Box>
            {loading ? (
              <Box sx={{ px: 2.5, pb: 2 }}>
                {[...Array(4)].map((_, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center' }}>
                    <Skeleton variant="circular" width={36} height={36} />
                    <Box sx={{ flex: 1 }}><Skeleton width="55%" /><Skeleton width="40%" height={12} /></Box>
                  </Box>
                ))}
              </Box>
            ) : recentAssign.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No recent vehicle activity</Typography>
              </Box>
            ) : (
              <Box>
                {recentAssign.map((a, i) => {
                  const isAssigned = a.event_type === 'assigned';
                  return (
                    <Box key={i}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.5 }}>
                        <Avatar sx={{
                          width: 36, height: 36, fontSize: 14, flexShrink: 0,
                          bgcolor: isAssigned ? '#dcfce7' : '#fef3c7',
                        }}>
                          {isAssigned
                            ? <AssignmentTurnedIn sx={{ fontSize: 18, color: '#16a34a' }} />
                            : <SwapHoriz sx={{ fontSize: 18, color: '#d97706' }} />}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.2 }}>
                            <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }} noWrap>
                              {a.vehicle_number}
                            </Typography>
                            <Chip
                              label={isAssigned ? 'Assigned' : 'Returned'}
                              color={isAssigned ? 'success' : 'warning'}
                              size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {a.user_name} · by {a.by_name}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, textAlign: 'right' }}>
                          {a.date}
                          {a.time && <><br />{a.time}</>}
                        </Typography>
                      </Box>
                      {i < recentAssign.length - 1 && <Divider sx={{ mx: 2.5 }} />}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* ── Live Vehicle Map ────────────────────────────────────────────────── */}
      <Box sx={{ mt: 3 }}>
        <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <Box sx={{ 
            px: 2.5, pt: 2.5, pb: 1, 
            display: 'flex', justifyContent: 'space-between', 
            alignItems: 'center', 
            flexWrap: 'wrap', 
            gap: 2 
          }}>
            <SectionHeader title="Live Vehicle Locations" icon={Map} />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={vehicleFilter}
                label="Filter by Status"
                onChange={(e) => setVehicleFilter(e.target.value)}
              >
                <MenuItem value="all">All Vehicles</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Divider />
          {loadingVehicles ? (
            <Skeleton variant="rectangular" height={500} />
          ) : (
            <VehicleMap 
              vehicles={vehicles} 
              filter={vehicleFilter} 
            />
          )}
        </Card>
      </Box>

      {/* Spin keyframe for refresh icon */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Box>
  );
}

// ── Vehicle Map Component ─────────────────────────────────────────────────────
const VehicleMap = ({ vehicles, filter }) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = React.useRef(null);
  const googleMapRef = React.useRef(null);
  const markersRef = React.useRef([]);

  // Filter vehicles based on selected filter
  const filteredVehicles = React.useMemo(() => {
    if (filter === 'all') return vehicles;
    return vehicles.filter(v => {
      if (filter === 'assigned') return v.status === 'assigned';
      if (filter === 'active') return v.status === 'active';
      if (filter === 'maintenance') return v.status === 'maintenance';
      return true;
    });
  }, [vehicles, filter]);

  // Load Google Maps script
  useEffect(() => {
    if (window.google && window.google.maps) {
      setMapLoaded(true);
      return;
    }

    const script = document.createElement('script');
    // TODO: Replace with your own Google Maps API key
    // Get it from: https://console.cloud.google.com/google/maps-apis/credentials
    const GOOGLE_MAPS_API_KEY = 'AIzaSyBEkY7qnLR8cl0P2_dhmy5ytsZTxCTG67M'; // Replace this
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
    script.async = true;
    script.defer = true;
    script.onload = () => setMapLoaded(true);
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  // Initialize map and markers
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !window.google) return;

    // Center on Bangalore
    const center = { lat: 12.9716, lng: 77.5946 };

    // Initialize map
    if (!googleMapRef.current) {
      googleMapRef.current = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: 12,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: true,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });
    }

    // Clear existing markers
    markersRef.current.forEach(marker => {
      marker.setMap(null);
      if (marker.overlay) {
        marker.overlay.setMap(null);
      }
    });
    markersRef.current = [];

    // Add markers for filtered vehicles
    filteredVehicles.forEach(vehicle => {
      if (!vehicle.latitude || !vehicle.longitude) return;

      const position = {
        lat: parseFloat(vehicle.latitude),
        lng: parseFloat(vehicle.longitude)
      };

      // Determine marker color based on status
      let markerColor = '#1565c0'; // default blue for active
      let statusLabel = vehicle.status || 'active';
      
      if (vehicle.status === 'assigned') {
        markerColor = '#16a34a'; // green for assigned
      } else if (vehicle.status === 'maintenance') {
        markerColor = '#dc2626'; // red for maintenance
      } else if (vehicle.status === 'active') {
        markerColor = '#1565c0'; // blue for active
      }

      // Create custom marker with scooter/vehicle icon
      const svgMarker = {
        path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
        fillColor: markerColor,
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: 1.8,
        anchor: new window.google.maps.Point(12, 22),
      };

      const marker = new window.google.maps.Marker({
        position,
        map: googleMapRef.current,
        title: vehicle.vehicle_number,
        icon: svgMarker,
        animation: window.google.maps.Animation.DROP,
      });

      // Create custom label overlay for vehicle number
      const labelDiv = document.createElement('div');
      labelDiv.style.cssText = `
        position: absolute;
        background: ${markerColor};
        color: white;
        padding: 2px 6px;
        border-radius: 3px;
        font-size: 10px;
        font-weight: bold;
        white-space: nowrap;
        border: 1px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        transform: translate(-50%, -100%);
        margin-top: -36px;
        pointer-events: none;
        z-index: 1000;
      `;
      labelDiv.textContent = vehicle.vehicle_number || '';

      class CustomOverlay extends window.google.maps.OverlayView {
        constructor(position, content) {
          super();
          this.position = position;
          this.content = content;
        }

        onAdd() {
          this.getPanes().floatPane.appendChild(this.content);
        }

        draw() {
          const projection = this.getProjection();
          const point = projection.fromLatLngToDivPixel(this.position);
          if (point) {
            this.content.style.left = point.x + 'px';
            this.content.style.top = point.y + 'px';
          }
        }

        onRemove() {
          if (this.content.parentElement) {
            this.content.parentElement.removeChild(this.content);
          }
        }
      }

      const overlay = new CustomOverlay(marker.getPosition(), labelDiv);
      overlay.setMap(googleMapRef.current);

      // Info window for vehicle details
      const infoContent = `
        <div style="padding: 8px; font-family: system-ui;">
          <div style="font-size: 14px; font-weight: bold; color: #1a237e; margin-bottom: 6px;">
            ${vehicle.vehicle_number}
          </div>
          <div style="font-size: 11px; color: #64748b; margin-bottom: 3px;">
            <strong>Model:</strong> ${vehicle.model || 'N/A'}
          </div>
          <div style="font-size: 11px; color: #64748b; margin-bottom: 3px;">
            <strong>Status:</strong> <span style="color: ${markerColor}; font-weight: 600; text-transform: uppercase;">${statusLabel}</span>
          </div>
          <div style="font-size: 11px; color: #64748b; margin-bottom: 3px;">
            <strong>Battery:</strong> ${vehicle.battery_pct || 0}%
          </div>
          ${vehicle.current_user ? `
            <div style="font-size: 11px; color: #64748b;">
              <strong>User:</strong> ${vehicle.current_user}
            </div>
          ` : ''}
        </div>
      `;

      const infoWindow = new window.google.maps.InfoWindow({
        content: infoContent
      });

      marker.addListener('click', () => {
        // Close all other info windows
        markersRef.current.forEach(m => {
          if (m.infoWindow) m.infoWindow.close();
        });
        infoWindow.open(googleMapRef.current, marker);
      });

      marker.infoWindow = infoWindow;
      marker.overlay = overlay;
      markersRef.current.push(marker);
    });

    // Adjust map bounds to show all markers
    if (filteredVehicles.length > 0 && markersRef.current.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      markersRef.current.forEach(marker => {
        bounds.extend(marker.getPosition());
      });
      googleMapRef.current.fitBounds(bounds);
      
      // Don't zoom in too much if there's only one marker
      const listener = window.google.maps.event.addListener(googleMapRef.current, 'idle', () => {
        if (googleMapRef.current.getZoom() > 15) {
          googleMapRef.current.setZoom(15);
        }
        window.google.maps.event.removeListener(listener);
      });
    }
  }, [mapLoaded, filteredVehicles]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        ref={mapRef}
        sx={{
          width: '100%',
          height: 500,
          bgcolor: '#e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {!mapLoaded && (
          <Typography variant="body2" color="text.secondary">
            Loading map...
          </Typography>
        )}
      </Box>
      
      {/* Vehicle count badge */}
      <Box sx={{
        position: 'absolute',
        bottom: 146,
        right: 16,
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        px: 2,
        py: 1,
        borderRadius: 1,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}>
        <Typography variant="body2" fontWeight={700} color="#1a237e">
          {filteredVehicles.length} Vehicle{filteredVehicles.length !== 1 ? 's' : ''}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {filter === 'all' ? 'All statuses' : filter.charAt(0).toUpperCase() + filter.slice(1)}
        </Typography>
      </Box>

      {/* Legend */}
      <Box sx={{
        position: 'absolute',
        bottom: 16,
        right: 16,
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        px: 2,
        py: 1.5,
        borderRadius: 1,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}>
        <Typography variant="caption" fontWeight={700} color="#1a237e" sx={{ mb: 1, display: 'block' }}>
          Legend
        </Typography>
        {[
          { label: 'Active', color: '#1565c0' },
          { label: 'Assigned', color: '#16a34a' },
          { label: 'Maintenance', color: '#dc2626' },
        ].map(({ label, color }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, border: '2px solid #fff' }} />
            <Typography variant="caption" color="text.secondary">{label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

